/**
 * OverviewTile — Aperçu annuel de la paie (Batch #14.250).
 */
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Users, Wallet, Landmark, AlertTriangle, CalendarClock, ArrowRight, Building2 } from "lucide-react";
import { fmtCAD, KpiCard, MONTHS_FR, FREQ_LABELS, StatusBadge, REMIT_STATUS, Section } from "./_shared";

export default function OverviewTile({ masterId, overview }) {
  const t = overview.totals || {};
  const s = overview.settings || {};
  const data = (overview.monthly || []).map((m) => ({ name: MONTHS_FR[m.m - 1], Brut: m.gross, Net: m.net, Employeur: m.employer }));
  const base = `/admin/paie/${masterId}`;
  const missing = [!s.legal_name && "nom légal de l'employeur", !s.cra_payroll_account && "n° de compte de retenues ARC (RP)", !s.rq_identification && "n° d'identification Revenu Québec"].filter(Boolean);

  return (
    <div className="space-y-4" data-testid="paie-overview">
      {missing.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="paie-overview-setup-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>Réglages incomplets : {missing.join(", ")}. <Link to={`${base}/reglages`} className="underline font-semibold">Compléter les réglages</Link></div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Employés actifs" value={`${overview.employees?.active ?? 0}`} sub={`${overview.employees?.total ?? 0} au total`} tone="sky" icon={Users} testId="paie-kpi-employees" />
        <KpiCard label="Brut cumulatif" value={fmtCAD(t.gross)} sub={`${t.nb_stubs || 0} bulletin(s) · ${t.nb_runs || 0} période(s)`} tone="orange" icon={Wallet} testId="paie-kpi-gross" />
        <KpiCard label="Net versé" value={fmtCAD(t.net)} sub={`Retenues : ${fmtCAD(t.deductions)}`} tone="emerald" testId="paie-kpi-net" />
        <KpiCard label="Cotisations employeur" value={fmtCAD(t.employer)} sub="RRQ · AE · RQAP · FSS · CNESST · CNT" tone="rose" icon={Building2} testId="paie-kpi-employer" />
        <KpiCard label="Coût total" value={fmtCAD(t.total_cost)} sub={`Impôt QC ${fmtCAD(t.qc_tax)} · Féd. ${fmtCAD(t.fed_tax)}`} tone="indigo" testId="paie-kpi-cost" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Section title={`Masse salariale ${overview.year} par mois`} className="lg:col-span-2" testId="paie-overview-chart">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => fmtCAD(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Brut" fill="#ea580c" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Net" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Employeur" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <div className="space-y-4">
          <Section title="Remises à faire" icon={Landmark} testId="paie-overview-remit">
            {(overview.remittances_due || []).length === 0 ? (
              <p className="text-xs text-slate-500">Aucune remise en retard ou à échéance proche.</p>
            ) : (
              <ul className="space-y-1.5">
                {overview.remittances_due.map((r) => (
                  <li key={`${r.key}-${r.total}`} className="flex items-center justify-between text-xs" data-testid={`paie-overview-due-${r.key}`}>
                    <span className="text-slate-700">{r.label} <span className="text-slate-400">· échéance {r.due}</span></span>
                    <span className="inline-flex items-center gap-2 font-mono font-semibold">{fmtCAD(r.total)} <StatusBadge status={r.status} map={REMIT_STATUS} /></span>
                  </li>
                ))}
              </ul>
            )}
            <Link to={`${base}/remises`} className="text-xs text-orange-700 font-semibold inline-flex items-center gap-1 hover:underline">Voir les remises <ArrowRight className="w-3 h-3" /></Link>
          </Section>

          <Section title="Périodes de paie" icon={CalendarClock} testId="paie-overview-runs">
            {overview.next_run && (
              <p className="text-xs text-slate-600">Prochaine paie : <b>{overview.next_run.pay_date}</b> ({FREQ_LABELS[overview.next_run.frequency]}) <StatusBadge status={overview.next_run.status} /></p>
            )}
            {(overview.pending_runs || []).length > 0 ? (
              <ul className="space-y-1.5">
                {overview.pending_runs.slice(0, 5).map((r) => (
                  <li key={r.id} className="flex items-center justify-between text-xs">
                    <Link to={`${base}/periodes/${r.id}`} className="text-slate-700 hover:underline" data-testid={`paie-overview-pending-${r.id}`}>{r.period_start} → {r.period_end}</Link>
                    <StatusBadge status={r.status} />
                  </li>
                ))}
              </ul>
            ) : <p className="text-xs text-slate-500">Aucune période en attente de calcul ou d'approbation.</p>}
            <Link to={`${base}/periodes`} className="text-xs text-orange-700 font-semibold inline-flex items-center gap-1 hover:underline">Gérer les périodes <ArrowRight className="w-3 h-3" /></Link>
          </Section>
        </div>
      </div>
    </div>
  );
}
